'use client';

/**
 * Wizard — Phase A3 multi-step form scaffold.
 *
 * Steps + indicator + back/next + draft + submit. Consumer owns the
 * per-step content + validation. The wizard handles:
 * - Step indicator (chips with check / current / pending state)
 * - Step navigation (Back / Next / Save draft / Submit)
 * - Per-step validation gating — `currentStep.canAdvance` controls
 *   whether the Next button is enabled
 * - Click-to-jump on VISITED steps only (forward jumps blocked)
 *
 * NOT included in v0.1:
 * - Conditional steps (skip rules) — consumer's `steps` array can be
 *   derived dynamically from current state; that's the cleanest path
 * - Review step — consumer renders their own review section as the
 *   final step
 * - Auto-save to draft state — wire `onSaveDraft` + persist externally
 *
 * Spec: `UX_PRODUCTION_ROADMAP.md` §A3 (Wizard).
 */

import React from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PageTitle } from '../typography';

export interface WizardStep {
  /** Stable id for the step. */
  id: string;
  /** Display label for the indicator chip. */
  label: string;
  /** Step body content (the consumer's form fields). */
  content: React.ReactNode;
  /**
   * Whether this step's data is valid + the user can advance to the
   * next step. When false, the Next/Submit button is disabled.
   */
  canAdvance?: boolean;
  /**
   * Optional description shown under the indicator when this step is
   * active.
   */
  description?: React.ReactNode;
}

export interface WizardProps {
  /** Ordered list of steps. */
  steps: ReadonlyArray<WizardStep>;
  /** Zero-based index of the current step. */
  currentStepIndex: number;
  /** Set of step indices the user has visited (controls click-to-jump). */
  visitedStepIndices: ReadonlySet<number>;
  /** Move to a different step (used by Back / Next / chip-click). */
  onStepChange: (newIndex: number) => void;
  /** Final-step submit handler. Replaces Next on the last step. */
  onSubmit: () => void;
  /** Optional Save draft handler — adds a "Save as draft" button. */
  onSaveDraft?: () => void;
  /** Optional Cancel handler — adds a "Cancel" button on the left. */
  onCancel?: () => void;
  /**
   * Loading flag — disables all nav buttons while a save / submit is
   * in flight.
   */
  loading?: boolean;
  /** Title shown above the indicator. */
  title?: string;
  className?: string;
}

export function Wizard({
  steps,
  currentStepIndex,
  visitedStepIndices,
  onStepChange,
  onSubmit,
  onSaveDraft,
  onCancel,
  loading,
  title,
  className,
}: WizardProps) {
  const current = steps[currentStepIndex];
  if (!current) return null;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;
  const canAdvance = current.canAdvance ?? true;

  return (
    <div className={cn('flex flex-col gap-4 pb-20', className)} data-wizard>
      {/* Header + indicator */}
      <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        {title && (
          <PageTitle className="mb-4 font-heading text-xl font-bold text-statura-ink">
            {title}
          </PageTitle>
        )}
        <ol role="list" aria-label="Wizard steps" className="flex flex-wrap items-center gap-2">
          {steps.map((step, idx) => {
            const isActive = idx === currentStepIndex;
            const isComplete = idx < currentStepIndex;
            const isVisited = visitedStepIndices.has(idx);
            const isClickable = isVisited && !isActive && !loading;
            return (
              <li key={step.id} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => (isClickable ? onStepChange(idx) : undefined)}
                  disabled={!isClickable}
                  aria-current={isActive ? 'step' : undefined}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition',
                    isActive
                      ? 'border-statura-green-accessible bg-statura-green-accessible text-white'
                      : isComplete
                        ? 'border-statura-green-accessible/40 bg-statura-green-accessible/10 text-statura-green-accessible'
                        : 'border-slate-200 bg-white text-slate-500',
                    isClickable &&
                      'hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
                    !isClickable && !isActive && 'cursor-default',
                  )}
                >
                  <span className="font-mono text-[10px]">{idx + 1}</span>
                  {isComplete && <Check className="h-3 w-3" aria-hidden="true" />}
                  <span>{step.label}</span>
                </button>
                {idx < steps.length - 1 && (
                  <span className="h-px w-3 bg-slate-200" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ol>
        {current.description && (
          <p className="mt-3 text-[12.5px] text-text-body">{current.description}</p>
        )}
      </header>

      {/* Step content */}
      <section
        aria-labelledby={`wizard-step-${current.id}`}
        className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
      >
        <h2 id={`wizard-step-${current.id}`} className="sr-only">
          {current.label}
        </h2>
        {current.content}
      </section>

      {/* Sticky nav bar */}
      <div
        role="region"
        aria-label="Wizard navigation"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-6 py-3 shadow-lg backdrop-blur"
      >
        <div className="mx-auto flex max-w-screen-2xl items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-statura-ink hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            {onSaveDraft && (
              <button
                type="button"
                onClick={onSaveDraft}
                disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-statura-ink hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible disabled:cursor-not-allowed disabled:opacity-50"
              >
                Save as draft
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onStepChange(currentStepIndex - 1)}
              disabled={isFirstStep || loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-statura-ink hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="h-3 w-3" aria-hidden="true" />
              Back
            </button>
            {isLastStep ? (
              <button
                type="button"
                onClick={onSubmit}
                disabled={!canAdvance || loading}
                className="inline-flex items-center gap-1.5 rounded-md bg-statura-green-accessible px-3 py-1.5 text-xs font-medium text-white hover:bg-statura-green-accessible-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible disabled:cursor-not-allowed disabled:opacity-50"
              >
                Submit
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onStepChange(currentStepIndex + 1)}
                disabled={!canAdvance || loading}
                className="inline-flex items-center gap-1.5 rounded-md bg-statura-green-accessible px-3 py-1.5 text-xs font-medium text-white hover:bg-statura-green-accessible-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
                <ChevronRight className="h-3 w-3" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
